<script>
export default {
  props: {
    scopes: {
      type: Object,
      default: () => ({}),
    },
    selectedScopes: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['change'],
  computed: {
    entities() {
      return Object.entries(this.scopes).map(([key, value]) => ({
        key,
        label: value.label,
        read: value.read,
        write: value.write,
        delete: value.delete,
      }));
    },
    allScopes() {
      return this.entities.flatMap(e =>
        [e.read, e.write, e.delete].filter(Boolean)
      );
    },
    allReadScopes() {
      return this.entities.map(e => e.read).filter(Boolean);
    },
    isAllSelected() {
      return this.allScopes.every(scope => this.selectedScopes.includes(scope));
    },
    isAllMeta() {
      return this.selectedScopes.includes('all');
    },
    isReadMeta() {
      return this.selectedScopes.includes('read');
    },
    extraWriteScopes() {
      return this.selectedScopes.filter(
        s => s !== 'all' && s !== 'read' && !s.endsWith(':read')
      );
    },
    displaySelectedCount() {
      if (this.isAllMeta) return this.allScopes.length;
      if (this.isReadMeta)
        return this.allReadScopes.length + this.extraWriteScopes.length;
      return this.selectedScopes.length;
    },
  },
  methods: {
    isScopeChecked(scope) {
      if (!scope) return false;
      if (this.isAllMeta) return true;
      if (this.isReadMeta && scope.endsWith(':read')) return true;
      return this.selectedScopes.includes(scope);
    },
    isScopeDisabled(scope) {
      if (!scope) return false;
      if (this.isAllMeta) return true;
      if (this.isReadMeta && scope.endsWith(':read')) return true;
      return false;
    },
    isEntityFullyChecked(entity) {
      const entityScopes = [entity.read, entity.write, entity.delete].filter(
        Boolean
      );
      return entityScopes.every(scope => this.isScopeChecked(scope));
    },
    isEntityDisabled(entity) {
      const entityScopes = [entity.read, entity.write, entity.delete].filter(
        Boolean
      );
      return entityScopes.every(scope => this.isScopeDisabled(scope));
    },
    toggleScope(scope) {
      if (!scope || this.isScopeDisabled(scope)) return;

      if (this.isReadMeta) {
        // In read meta mode, user can toggle individual write/delete scopes
        const nonMetaScopes = this.selectedScopes.filter(s => s !== 'read');
        const newScopes = nonMetaScopes.includes(scope)
          ? ['read', ...nonMetaScopes.filter(s => s !== scope)]
          : ['read', ...nonMetaScopes, scope];
        this.$emit('change', newScopes);
        return;
      }

      const newScopes = this.selectedScopes.includes(scope)
        ? this.selectedScopes.filter(s => s !== scope)
        : [...this.selectedScopes, scope];
      this.$emit('change', newScopes);
    },
    toggleEntityAll(entity) {
      if (this.isEntityDisabled(entity)) return;

      const entityScopes = [entity.read, entity.write, entity.delete].filter(
        Boolean
      );

      if (this.isReadMeta) {
        // In read meta mode, only toggle write/delete scopes
        const toggleable = entityScopes.filter(s => !s.endsWith(':read'));
        const nonMetaScopes = this.selectedScopes.filter(s => s !== 'read');
        const allToggleableSelected = toggleable.every(s =>
          nonMetaScopes.includes(s)
        );
        const newNonMeta = allToggleableSelected
          ? nonMetaScopes.filter(s => !toggleable.includes(s))
          : [...new Set([...nonMetaScopes, ...toggleable])];
        this.$emit('change', ['read', ...newNonMeta]);
        return;
      }

      const allSelected = entityScopes.every(scope =>
        this.selectedScopes.includes(scope)
      );

      if (allSelected) {
        const newScopes = this.selectedScopes.filter(
          s => !entityScopes.includes(s)
        );
        this.$emit('change', newScopes);
      } else {
        const newScopes = [
          ...new Set([...this.selectedScopes, ...entityScopes]),
        ];
        this.$emit('change', newScopes);
      }
    },
    setFullAccess() {
      if (this.isAllMeta) {
        this.$emit('change', []);
      } else {
        this.$emit('change', ['all']);
      }
    },
    setReadOnly() {
      if (this.isReadMeta) {
        this.$emit('change', []);
      } else {
        this.$emit('change', ['read']);
      }
    },
    selectAll() {
      this.$emit('change', this.allScopes);
    },
    clearAll() {
      this.$emit('change', []);
    },
  },
};
</script>

<template>
  <div class="space-y-4">
    <!-- Meta-scope toggles -->
    <div class="flex items-center gap-3">
      <button
        type="button"
        class="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors"
        :class="
          isAllMeta
            ? 'bg-woot-500 text-white'
            : 'bg-n-alpha-2 text-n-slate-12 hover:bg-n-alpha-3'
        "
        @click="setFullAccess"
      >
        {{ $t('KAPTION_ACCESS_TOKENS.FORM.SCOPES.META_ALL') }}
      </button>
      <button
        type="button"
        class="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors"
        :class="
          isReadMeta && !isAllMeta
            ? 'bg-woot-500 text-white'
            : 'bg-n-alpha-2 text-n-slate-12 hover:bg-n-alpha-3'
        "
        @click="setReadOnly"
      >
        {{ $t('KAPTION_ACCESS_TOKENS.FORM.SCOPES.META_READ') }}
      </button>
      <span class="ml-auto flex gap-2">
        <button
          type="button"
          class="text-xs text-woot-500 hover:text-woot-600"
          @click="selectAll"
        >
          {{ $t('KAPTION_ACCESS_TOKENS.FORM.SCOPES.SELECT_ALL') }}
        </button>
        <span class="text-xs text-n-slate-11">|</span>
        <button
          type="button"
          class="text-xs text-woot-500 hover:text-woot-600"
          @click="clearAll"
        >
          {{ $t('KAPTION_ACCESS_TOKENS.FORM.SCOPES.CLEAR_ALL') }}
        </button>
      </span>
    </div>

    <!-- Meta-scope description -->
    <p v-if="isAllMeta" class="text-xs text-n-slate-11">
      {{ $t('KAPTION_ACCESS_TOKENS.FORM.SCOPES.META_ALL_DESC') }}
    </p>
    <p v-else-if="isReadMeta" class="text-xs text-n-slate-11">
      {{ $t('KAPTION_ACCESS_TOKENS.FORM.SCOPES.META_READ_DESC') }}
    </p>

    <div class="border border-n-weak rounded-lg overflow-hidden">
      <table class="w-full">
        <thead>
          <tr class="bg-n-alpha-2 border-b border-n-weak">
            <th class="px-4 py-3 text-left text-sm font-medium text-n-slate-12">
              {{ $t('KAPTION_ACCESS_TOKENS.FORM.SCOPES.TABLE_HEADERS.ENTITY') }}
            </th>
            <th
              class="px-4 py-3 text-center text-sm font-medium text-n-slate-12 w-20"
            >
              {{ $t('KAPTION_ACCESS_TOKENS.FORM.SCOPES.TABLE_HEADERS.READ') }}
            </th>
            <th
              class="px-4 py-3 text-center text-sm font-medium text-n-slate-12 w-20"
            >
              {{ $t('KAPTION_ACCESS_TOKENS.FORM.SCOPES.TABLE_HEADERS.WRITE') }}
            </th>
            <th
              class="px-4 py-3 text-center text-sm font-medium text-n-slate-12 w-20"
            >
              {{ $t('KAPTION_ACCESS_TOKENS.FORM.SCOPES.TABLE_HEADERS.DELETE') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="entity in entities"
            :key="entity.key"
            class="border-b border-n-weak last:border-b-0 hover:bg-n-alpha-1"
          >
            <td class="px-4 py-3">
              <div class="flex items-center gap-2">
                <input
                  :id="`entity-${entity.key}`"
                  type="checkbox"
                  :checked="isEntityFullyChecked(entity)"
                  :disabled="isEntityDisabled(entity)"
                  class="h-4 w-4 rounded border-n-weak text-woot-500 focus:ring-woot-500 disabled:opacity-50"
                  @change="toggleEntityAll(entity)"
                />
                <label
                  :for="`entity-${entity.key}`"
                  class="text-sm font-medium text-n-slate-12 cursor-pointer"
                >
                  {{ entity.label }}
                </label>
              </div>
            </td>
            <td class="px-4 py-3 text-center">
              <input
                v-if="entity.read"
                :id="`scope-${entity.read}`"
                type="checkbox"
                :checked="isScopeChecked(entity.read)"
                :disabled="isScopeDisabled(entity.read)"
                class="h-4 w-4 rounded border-n-weak text-woot-500 focus:ring-woot-500 disabled:opacity-50"
                @change="toggleScope(entity.read)"
              />
              <span v-else class="text-n-slate-11">—</span>
            </td>
            <td class="px-4 py-3 text-center">
              <input
                v-if="entity.write"
                :id="`scope-${entity.write}`"
                type="checkbox"
                :checked="isScopeChecked(entity.write)"
                :disabled="isScopeDisabled(entity.write)"
                class="h-4 w-4 rounded border-n-weak text-woot-500 focus:ring-woot-500 disabled:opacity-50"
                @change="toggleScope(entity.write)"
              />
              <span v-else class="text-n-slate-11">—</span>
            </td>
            <td class="px-4 py-3 text-center">
              <input
                v-if="entity.delete"
                :id="`scope-${entity.delete}`"
                type="checkbox"
                :checked="isScopeChecked(entity.delete)"
                :disabled="isScopeDisabled(entity.delete)"
                class="h-4 w-4 rounded border-n-weak text-woot-500 focus:ring-woot-500 disabled:opacity-50"
                @change="toggleScope(entity.delete)"
              />
              <span v-else class="text-n-slate-11">—</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="displaySelectedCount > 0" class="text-xs text-n-slate-11">
      {{
        $t('KAPTION_ACCESS_TOKENS.FORM.SCOPES.SELECTED_COUNT', {
          count: displaySelectedCount,
        })
      }}
    </div>
  </div>
</template>
